// Unified Escape key handler for closing/collapsing topmost overlay
import { getPreviewState } from '../../../modules/files/file-list.dom/preview-popup/state.js';
import { hidePreviewPopup, closeLinkPopup } from '../../../modules/files/file-list.dom/preview-popup/popup/preview-popup.js';
import { getAudioPlayerElement, getAudioPlayerCollapsed } from '../../../modules/files/file-list.dom/audio-player.js';
import { animatePlayerCollapse } from '../../../modules/files/file-list.dom/audio-player/player-ui.js';

function getElementZIndex(element) {
	if (!element || !element.length) return -1;
	const computedStyle = window.getComputedStyle(element[0]);
	const zIndex = parseInt(computedStyle.zIndex, 10);
	// If z-index is auto or invalid, check parent or use default
	if (isNaN(zIndex) || zIndex === 0) {
		// Check if element has inline style
		const inlineZIndex = parseInt(element.css('z-index'), 10);
		if (!isNaN(inlineZIndex) && inlineZIndex !== 0) {
			return inlineZIndex;
		}
		// Use CSS class-based defaults: audio-player = 10000, preview-popup = 1070, link-popup = 1080
		if (element.hasClass('audio-player-overlay')) {
			return 10000;
		}
		if (element.hasClass('preview-popup__link-popup')) {
			return 1080;
		}
		if (element.hasClass('preview-popup-overlay')) {
			return 1070;
		}
		return -1;
	}
	return zIndex;
}

function handleEscapeKey(e) {
	if (e.key !== 'Escape') return;
	
	// Debug logging
	if (window.debugMode === '1') {
		console.log('[Escape Handler] Escape pressed', {
			target: e.target,
			currentTarget: e.currentTarget
		});
	}
	
	// First check Bootstrap modals (highest priority)
	try {
		const $modals = window.jQuery ? window.jQuery('.modal.show') : null;
		if ($modals && $modals.length) {
			// Close the last (topmost) shown modal only
			const el = $modals[$modals.length - 1];
			try { 
				(window.bootstrap && window.bootstrap.Modal.getInstance(el) || window.bootstrap.Modal.getOrCreateInstance(el)).hide(); 
			} catch(_) {}
			e.preventDefault();
			e.stopPropagation();
			e.stopImmediatePropagation(); // Stop all other handlers
			if (window.debugMode === '1') {
				console.log('[Escape Handler] Closed Bootstrap modal');
			}
			return;
		}
	} catch(_) {}
	
	const previewState = getPreviewState();
	const previewElement = previewState.previewPopupElement;
	const audioPlayerElement = getAudioPlayerElement();
	const linkPopupElement = window.jQuery ? window.jQuery('.preview-popup__link-popup') : null;
	
	// Check which elements are visible
	// Preview is visible if element exists in DOM (more reliable than class check)
	const previewExists = previewElement && previewElement.length && document.body.contains(previewElement[0]);
	const previewHasVisibleClass = previewElement && previewElement.length && previewElement.hasClass('preview-popup-overlay--visible');
	// If element exists in DOM, consider it visible (class check is secondary)
	const previewVisible = previewExists;
	
	const audioVisible = audioPlayerElement && audioPlayerElement.length && 
		audioPlayerElement.hasClass('audio-player-overlay--visible');
	const linkPopupVisible = linkPopupElement && linkPopupElement.length && 
		linkPopupElement.hasClass('preview-popup__link-popup--visible');
	
	// Debug logging
	if (window.debugMode === '1') {
		console.log('[Escape Handler] Visibility check', {
			previewExists,
			previewHasVisibleClass,
			previewVisible,
			audioVisible,
			linkPopupVisible,
			previewElement: previewElement ? previewElement.length : 0,
			previewElementInDOM: previewExists
		});
	}
	
	if (!previewVisible && !audioVisible && !linkPopupVisible) {
		if (window.debugMode === '1') {
			console.log('[Escape Handler] No visible overlays, returning');
		}
		return;
	}
	
	// Get z-index values (computed style)
	const previewZIndex = previewVisible ? getElementZIndex(previewElement) : -1;
	const audioZIndex = audioVisible ? getElementZIndex(audioPlayerElement) : -1;
	const linkPopupZIndex = linkPopupVisible ? getElementZIndex(linkPopupElement) : -1;
	
	// Find the highest z-index (topmost element)
	const maxZIndex = Math.max(previewZIndex, audioZIndex, linkPopupZIndex);
	
	if (window.debugMode === '1') {
		console.log('[Escape Handler] Z-index values', {
			previewZIndex,
			audioZIndex,
			linkPopupZIndex,
			maxZIndex
		});
	}
	
	// Handle only the topmost element
	if (linkPopupZIndex === maxZIndex && linkPopupVisible) {
		// Link popup is on top - close it
		e.preventDefault();
		e.stopPropagation();
		e.stopImmediatePropagation(); // Stop all other handlers
		if (window.debugMode === '1') {
			console.log('[Escape Handler] Closing link popup');
		}
		closeLinkPopup();
		return;
	} else if (previewZIndex === maxZIndex && previewVisible) {
		// Preview is on top - close it
		e.preventDefault();
		e.stopPropagation();
		e.stopImmediatePropagation(); // Stop all other handlers
		if (window.debugMode === '1') {
			console.log('[Escape Handler] Closing preview popup');
		}
		hidePreviewPopup();
		return;
	} else if (audioZIndex === maxZIndex && audioVisible) {
		// Audio player is on top - collapse it if expanded
		e.preventDefault();
		e.stopPropagation();
		e.stopImmediatePropagation(); // Stop all other handlers
		if (window.debugMode === '1') {
			console.log('[Escape Handler] Collapsing audio player');
		}
		const isCollapsed = getAudioPlayerCollapsed();
		if (!isCollapsed) {
			animatePlayerCollapse(true, null);
		}
		return;
	}
	
	// Fallback: handle visible elements even if z-index couldn't be determined
	if (linkPopupVisible) {
		e.preventDefault();
		e.stopPropagation();
		e.stopImmediatePropagation(); // Stop all other handlers
		if (window.debugMode === '1') {
			console.log('[Escape Handler] Fallback: Closing link popup');
		}
		closeLinkPopup();
		return;
	}
	if (previewVisible) {
		e.preventDefault();
		e.stopPropagation();
		e.stopImmediatePropagation(); // Stop all other handlers
		if (window.debugMode === '1') {
			console.log('[Escape Handler] Fallback: Closing preview popup');
		}
		hidePreviewPopup();
		return;
	}
	if (audioVisible) {
		e.preventDefault();
		e.stopPropagation();
		e.stopImmediatePropagation(); // Stop all other handlers
		if (window.debugMode === '1') {
			console.log('[Escape Handler] Fallback: Collapsing audio player');
		}
		const isCollapsed = getAudioPlayerCollapsed();
		if (!isCollapsed) {
			animatePlayerCollapse(true, null);
		}
		return;
	}
}

let escapeHandlerAttached = false;

export function attachEscapeHandler() {
	if (escapeHandlerAttached) return;
	// Use capture phase to handle Escape before other handlers
	document.addEventListener('keydown', handleEscapeKey, true);
	escapeHandlerAttached = true;
}

export function detachEscapeHandler() {
	if (!escapeHandlerAttached) return;
	document.removeEventListener('keydown', handleEscapeKey, true);
	escapeHandlerAttached = false;
}

// Auto-attach on module load
if (typeof window !== 'undefined' && window.$) {
	$(document).ready(function() {
		attachEscapeHandler();
	});
}

